import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { iHome, iShield } from '@pte/react-icons';
import { Icon } from '../components/ui/Icon';
import { useCore } from '../hooks/useCore';
/**
 * NotAuthorizedPage Component
 *
 * A user-friendly 403 error page that displays when users try to access
 * resources they don't have permission to view or modify.
 */
export const NotAuthorizedPage: React.FC = () => {
  const navigate = useNavigate();
  const { gotoPtEApp } = useCore();

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <div
      className='min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center px-4 sm:px-6 lg:px-8'
      data-pte-name='not-authorized-page'
    >
      <div className='max-w-md w-full space-y-8 text-center'>
        {/* 403 Illustration */}
        <div className='mb-8' data-pte-name='not-authorized-page-illustration'>
          <div className='text-9xl font-bold text-red-600 mb-4'>403</div>
          <div className='w-24 h-1 bg-red-600 mx-auto rounded-full'></div>
        </div>

        {/* Shield Icon */}
        <div
          className='flex justify-center mb-6'
          data-pte-name='not-authorized-page-shield'
        >
          <div className='w-20 h-20 bg-red-100 rounded-full flex items-center justify-center'>
            <Icon icon={iShield} />
          </div>
        </div>

        {/* Error Message */}
        <div className='space-y-4' data-pte-name='not-authorized-page-message'>
          <h1 className='text-3xl font-bold text-gray-900'>Access Denied</h1>
          <p className='text-lg text-gray-600 leading-relaxed'>
            Sorry, you don't have permission to access this resource. You may
            need to log in with appropriate credentials or contact your
            administrator.
          </p>
        </div>

        {/* Action Buttons */}
        <div
          className='space-y-4 pt-6'
          data-pte-name='not-authorized-page-actions'
        >
          <div className='flex flex-col sm:flex-row gap-3 justify-center'>
            <Button
              onClick={gotoPtEApp}
              className='inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200'
              data-pte-name='not-authorized-page-btn-pte-app'
            >
              Go to PTE App
            </Button>

            <Button
              onClick={handleGoHome}
              className='inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200'
              data-pte-name='not-authorized-page-btn-home'
            >
              <Icon icon={iHome} className='w-5 h-5 mr-2' />
              Go Home
            </Button>
          </div>
        </div>

        {/* Additional Information */}
        <div
          className='pt-8 border-t border-gray-200'
          data-pte-name='not-authorized-page-additional-info'
        >
          <div className='bg-yellow-50 border border-yellow-200 rounded-md p-4'>
            <div className='flex items-start'>
              <div className='flex-shrink-0'>
                <Icon icon={iShield} className='w-5 h-5 text-yellow-600' />
              </div>
              <div className='ml-3'>
                <h3 className='text-sm font-medium text-yellow-800'>
                  Why am I seeing this?
                </h3>
                <div className='mt-2 text-sm text-yellow-700'>
                  <ul className='list-disc list-inside space-y-1'>
                    <li>You may not be logged in</li>
                    <li>Your session may have expired</li>
                    <li>You don't have the required permissions</li>
                    <li>The resource requires a different access level</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className='pt-6' data-pte-name='not-authorized-page-help'>
          <p className='text-sm text-gray-500 mb-4'>
            Need assistance? Contact your administrator or try signing in again.
          </p>
        </div>
      </div>
    </div>
  );
};
